import * as React from 'react'
import { Link, useParams } from 'react-router-dom'
import { CalendarClock, ChevronLeft, MapPin, Store, Users } from 'lucide-react'
import { useOwnVendorEvent, useProfileChecklist } from '@/hooks/useVendors'
import { isApiError, requestErrorMessage } from '@/helpers/apiClient'
import { fmt12, formatEventDate, timeRangeLabel } from '@/lib/eventTime'
import { Badge, StatusBadge } from '@/components/ui/badge'
import { LoadingState, ErrorState } from '@/components/ui/states'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader, SectionHeader } from '@/components/ui/page-header'
import { PlanPhase } from '@/components/public/PlanPhase'
import { Facts } from '@/components/ui/facts'
import type { VendorEventDto } from '@lib/dto/vendor-event.dto'
import { vendorEventReasons, vendorEventStatus } from './vendorEvents'
import { ProfileChecklistCard } from '@/components/vendor-profile/ProfileChecklistCard'

function Rentals({ rentals }: { rentals: VendorEventDto['rentals'] }): React.ReactElement | null {
  if (rentals.length === 0) return null
  return (
    <section className="space-y-3">
      <SectionHeader title="Your furniture" />
      <ul className="m-0 list-none divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface p-0">
        {/* Keyed by position too: a company may own two pieces of one name. */}
        {rentals.map((rental, i) => (
          <li key={`${rental.itemName}-${String(i)}`} className="flex items-baseline justify-between gap-4 px-4 py-2.5">
            <span className="text-sm text-ink">{rental.itemName}</span>
            {rental.removed ? (
              <Badge tone="neutral">Taken off</Badge>
            ) : (
              <span className="font-mono text-sm tabular-nums text-ink">×{rental.quantity}</span>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

function RunOfTheDay({ timeline }: { timeline: VendorEventDto['timeline'] }): React.ReactElement | null {
  if (timeline.length === 0) return null
  return (
    <section className="space-y-3">
      <SectionHeader title="Run of the day" />
      <ol className="m-0 list-none divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface p-0">
        {timeline.map((moment, i) => (
          <li key={`${moment.startsAt}-${String(i)}`} className="flex items-baseline gap-4 px-4 py-2.5">
            <span className="w-20 shrink-0 font-mono text-sm tabular-nums text-ink-muted">{fmt12(moment.startsAt)}</span>
            <span className="min-w-0 whitespace-pre-line break-words text-sm text-ink">{moment.label}</span>
          </li>
        ))}
      </ol>
    </section>
  )
}

function Summary({ event }: { event: VendorEventDto }): React.ReactElement {
  const times = timeRangeLabel(event.startsAt, event.endsAt)
  const when = event.date === null ? null : [formatEventDate(event.date), times].filter((part) => part !== '').join(', ')
  const guests = event.guestCount === 1 ? '1 guest' : `${String(event.guestCount)} guests`
  return (
    <Facts
      layout="stack"
      className="gap-2 text-sm"
      facts={[
        { icon: <CalendarClock />, label: 'When', value: when, empty: 'Date not set' },
        { icon: <MapPin />, label: 'Where', value: `${event.venueName} · ${event.organizationName}`, empty: 'No venue' },
        { icon: <Users />, label: 'Guests', value: event.guestCount > 0 ? guests : null, empty: 'Guest count not set yet' },
        {
          icon: <Store />,
          label: 'You are here for',
          value: vendorEventReasons(event.services, event.rentals.some((r) => !r.removed)),
          empty: 'Nothing any more',
        },
      ]}
    />
  )
}

/**
 * One event as a vendor that is not its planner sees it (docs/planner-vendors.md,
 * decision 17): its day, guest count, rooms, run of the day and plans, and its
 * own rented furniture — read-only. Everything else about the event is the
 * venue's, the couple's and the planner's.
 */
export function VendorEventPage(): React.ReactElement {
  const { eventId } = useParams()
  const query = useOwnVendorEvent(eventId ?? '')
  // Beside the event while the company's profile has steps to go; the event alone after.
  const checklist = useProfileChecklist()
  const showChecklist = checklist.data?.done === false

  function body(): React.ReactElement {
    if (query.isPending) return <LoadingState label="Loading the event…" />
    if (query.isError) {
      const message =
        isApiError(query.error) && query.error.status === 404
          ? 'This event is not in your Events list. Your company may have been taken off it.'
          : requestErrorMessage(query.error)
      return <ErrorState message={message} onRetry={() => void query.refetch()} />
    }
    const event = query.data
    return (
      <div className="space-y-8">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <PageHeader title={event.name} />
            <StatusBadge status={vendorEventStatus(event)} />
          </div>
          <Summary event={event} />
        </div>
        <Rentals rentals={event.rentals} />
        <RunOfTheDay timeline={event.timeline} />
        <section className="space-y-3">
          <SectionHeader title="Plans" />
          {event.phases.length === 0 ? (
            <p className="m-0 text-sm text-ink-muted">Nothing has been planned for this event yet.</p>
          ) : (
            <div className="space-y-10">
              {event.phases.map((phase) => (
                <PlanPhase key={phase.id} phase={phase} />
              ))}
            </div>
          )}
        </section>
      </div>
    )
  }

  return (
    <PageContainer width="wide" className="space-y-6">
      <Link
        to="/vendor/events"
        className="inline-flex items-center gap-1 text-sm font-medium text-ink-muted transition-colors hover:text-ink"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden /> All events
      </Link>
      {showChecklist ? (
        <div className="space-y-6 lg:grid lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start lg:gap-8 lg:space-y-0">
          <div className="lg:sticky lg:top-6 lg:order-2">
            <ProfileChecklistCard />
          </div>
          <div className="min-w-0 lg:order-1">{body()}</div>
        </div>
      ) : (
        body()
      )}
    </PageContainer>
  )
}
